/**
 * Help-topic metadata shared by the in-app help dialog and the chat assistant.
 *
 * Both surfaces render the same `helpDialog.*` translation keys via the same
 * `HelpStepsComponent`. This registry is the single source of truth that maps
 * a topic ID to the i18n keys + step count that drive the renderer.
 *
 * Backend (`HelpTopicMatcherService`) matches user messages against keywords
 * and returns `HelpRef` objects referencing entries from this registry. The
 * frontend chat dialog renders the matching `HelpStepsComponent` inline —
 * the help content itself is never copy-pasted, paraphrased, or AI-generated.
 */
export type HelpTopicId =
  | 'socialLogin.microsoft'
  | 'socialLogin.google'
  | 'socialLogin.apple'
  | 'socialLogin.linkedin'
  | 'directory.microsoft'
  | 'directory.google';

export interface HelpTopicMetadata {
  /** i18n key for the dialog/section title. */
  titleKey: string;
  /** Base i18n key from which `<baseKey>.s<N>.{title,body}` keys are derived. */
  baseKey: string;
  /** Number of `s1..sN` step keys defined in the translation files. */
  stepCount: number;
  /** Material icon shown next to the title. */
  icon: string;
  /** i18n key for the help button tooltip. */
  tooltipKey: string;
}

export const HELP_TOPIC_REGISTRY: Record<HelpTopicId, HelpTopicMetadata> = {
  'socialLogin.microsoft': {
    titleKey: 'helpDialog.socialLogin.microsoft.title',
    baseKey: 'helpDialog.socialLogin.microsoft',
    stepCount: 6,
    icon: 'help_outline',
    tooltipKey: 'helpDialog.openTooltip.microsoft',
  },
  'socialLogin.google': {
    titleKey: 'helpDialog.socialLogin.google.title',
    baseKey: 'helpDialog.socialLogin.google',
    stepCount: 5,
    icon: 'help_outline',
    tooltipKey: 'helpDialog.openTooltip.google',
  },
  'socialLogin.apple': {
    titleKey: 'helpDialog.socialLogin.apple.title',
    baseKey: 'helpDialog.socialLogin.apple',
    stepCount: 5,
    icon: 'help_outline',
    tooltipKey: 'helpDialog.openTooltip.apple',
  },
  'socialLogin.linkedin': {
    titleKey: 'helpDialog.socialLogin.linkedin.title',
    baseKey: 'helpDialog.socialLogin.linkedin',
    stepCount: 5,
    icon: 'help_outline',
    tooltipKey: 'helpDialog.openTooltip.linkedin',
  },
  'directory.microsoft': {
    titleKey: 'helpDialog.directory.microsoft.title',
    baseKey: 'helpDialog.directory.microsoft',
    stepCount: 6,
    icon: 'help_outline',
    tooltipKey: 'helpDialog.openTooltip.directoryMicrosoft',
  },
  'directory.google': {
    titleKey: 'helpDialog.directory.google.title',
    baseKey: 'helpDialog.directory.google',
    stepCount: 1,
    icon: 'help_outline',
    tooltipKey: 'helpDialog.openTooltip.directoryGoogle',
  },
};

/**
 * Returns the metadata for a topic ID, or `null` for unknown IDs.
 * Useful for defensive frontend rendering: if the backend ever ships a topic
 * the frontend doesn't yet know about, the chat falls back silently rather
 * than throwing.
 */
export function getHelpTopicMetadata(topicId: string): HelpTopicMetadata | null {
  return (HELP_TOPIC_REGISTRY as Record<string, HelpTopicMetadata>)[topicId] ?? null;
}

/**
 * Reference to a help topic, returned by the orchestrator when the user asks
 * about a documented setup flow. The chat UI renders the matching
 * `HelpStepsComponent` inline below the assistant's intro message.
 */
export interface HelpRef {
  topicId: HelpTopicId;
}
